import { useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { RequireAuth } from '../features/auth/RequireAuth';
import { Shell } from '../features/shell/Shell';
import { SignInPage } from '../routes/auth/SignInPage';
import { SignUpPage } from '../routes/auth/SignUpPage';
import { ForgotPasswordPage } from '../routes/auth/ForgotPasswordPage';
import { ResetPasswordPage } from '../routes/auth/ResetPasswordPage';
import { VerifyEmailPage } from '../routes/auth/VerifyEmailPage';
import { AcceptInvitePage } from '../routes/auth/AcceptInvitePage';
import { HomePage } from '../routes/HomePage';
import { ReportsIndex, ReportsPage } from '../routes/ReportsPage';
import { OnboardingPage } from '../routes/OnboardingPage';
import { ProjectViewsPage } from '../routes/board/ProjectViewsPage';
import { TicketPage } from '../routes/ticket/TicketPage';
import { TicketModal, backgroundOf } from '../features/ticket/TicketModal';
import { SearchPage } from '../routes/SearchPage';
import { ArchivePage } from '../routes/ArchivePage';
import { DraftsPage } from '../routes/DraftsPage';
import { ProjectSettingsPage } from '../routes/settings/ProjectSettingsPage';
import { CompanySettingsPage } from '../routes/settings/CompanySettingsPage';
import { UserSettingsPage } from '../routes/settings/UserSettingsPage';
import { McpSetupPage } from '../routes/McpSetupPage';
import { NotFoundPage, ForbiddenPage, ServerErrorPage } from '../routes/ErrorPages';
import { InboxPage } from '../routes/InboxPage';
import { AllProjectsPage } from '../routes/AllProjectsPage';
import { ExtensionHandoffPage } from '../routes/ExtensionHandoffPage';
import { HandoffPage } from '../routes/HandoffPage';
import { WidgetHarnessPage } from '../routes/WidgetHarnessPage';
import { CapturingPage } from '../routes/CapturingPage';
import { OAuthExtensionPage } from '../routes/OAuthExtensionPage';
import { WidgetSignInPage } from '../routes/WidgetSignInPage';
import { OAuthAuthorizePage } from '../routes/OAuthAuthorizePage';
import { BillingSuccessPage } from '../routes/BillingSuccessPage';
import { ReportLinkPage } from '../routes/public/ReportLinkPage';
import { UninstallFeedbackPage } from '../routes/UninstallFeedbackPage';
import { AdminLayout } from '../routes/admin/AdminLayout';
import { RequireStaff } from '../features/auth/RequireStaff';
import { RequireV2Recorder } from '../features/auth/RequireV2Recorder';
import { RecordingsPage } from '../routes/RecordingsPage';
import { RecordingReviewPage } from '../routes/RecordingReviewPage';

export function AppRouter() {
  // A ticket opened from the board carries the board as its background
  // (Jason, 18 Oct): the board stays rendered, and the ticket opens over it.
  // Without one (a link, a refresh, a new tab) the ticket is its own page.
  // A refresh keeps the history entry's state, background and all, so the
  // entry the page loaded on never counts as opened over the board.
  const location = useLocation();
  const [loadedOn] = useState(location.key);
  const background = location.key === loadedOn ? null : backgroundOf(location);
  return (
    <>
    {/* Under an open ticket the page is inert: no tabbing into it, nothing of
        it read out, and its buttons not taken for the ticket's. Laid out as
        if this wrapper were not there. */}
    <div inert={background !== null} aria-hidden={background !== null ? true : undefined} style={{ display: 'contents' }}>
    <Routes location={background ?? location}>
      <Route path="/signin" element={<SignInPage />} />
      <Route path="/signup" element={<SignUpPage />} />
      <Route path="/forgot" element={<ForgotPasswordPage />} />
      <Route path="/reset" element={<ResetPasswordPage />} />
      {/* Email verification landing — public (the user isn't signed in
          yet when they click the link from their inbox). Auto-POSTs the
          token to /auth/verify-email on mount and renders the result. */}
      <Route path="/verify" element={<VerifyEmailPage />} />
      <Route path="/invite/:token" element={<AcceptInvitePage />} />
      {/* Public, no auth required — Chrome opens this when the extension
          is uninstalled, the user isn't necessarily signed in. */}
      <Route path="/x/uninstall" element={<UninstallFeedbackPage />} />
      {/* Toado-hosted bug report form. Public by definition: the visitor
          followed a link or scanned a QR code and has no account here. */}
      <Route path="/r/:key" element={<ReportLinkPage />} />
      {/* Local dev harness for the widget SDK. DEV-only so it never ships
          to prod; loads the real loader for a pasted key. */}
      {import.meta.env.DEV && <Route path="/dev/widget" element={<WidgetHarnessPage />} />}
      {/* Visual and interaction harness for the shared floating-control shell.
          It reuses the real recorder page but stays unreachable in a
          production build. */}
      {import.meta.env.DEV && <Route path="/dev/recorder-control" element={<RecordingsPage />} />}

      <Route element={<RequireAuth />}>
        {/* Standalone post-checkout thank-you page — sits inside auth
            (need a session to query plan) but outside Shell (no sidebar
            or top bar; centered card layout). */}
        <Route path="/billing/success" element={<BillingSuccessPage />} />
        <Route element={<Shell />}>
          <Route path="/" element={<HomePage />} />
          <Route path="/reports" element={<ReportsIndex />} />
          <Route path="/reports/:section" element={<ReportsPage />} />
          <Route path="/onboarding" element={<OnboardingPage />} />
          <Route path="/inbox/:filter" element={<InboxPage />} />
          <Route path="/projects" element={<AllProjectsPage />} />
          <Route path="/projects/:projectId" element={<Navigate to="board" replace />} />
          <Route path="/projects/:projectId/board" element={<ProjectViewsPage />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/projects/:projectId/search" element={<SearchPage />} />
          <Route path="/projects/:projectId/archive" element={<ArchivePage />} />
          <Route path="/projects/:projectId/drafts" element={<DraftsPage />} />
          <Route path="/projects/:projectId/settings" element={<ProjectSettingsPage />} />
          <Route path="/tickets/:ticketId" element={<TicketPage />} />
          <Route path="/company/settings/*" element={<CompanySettingsPage />} />
          <Route path="/me/settings/*" element={<UserSettingsPage />} />
          <Route path="/mcp" element={<McpSetupPage />} />
          <Route path="/x/ticket" element={<ExtensionHandoffPage />} />
          {/* Widget authenticated handoff (TOADO-136): claims a parked
              capture token and drops the signed-in user into the draft.
              Inside RequireAuth, so an unauthenticated arrival signs in and
              returns here to complete the claim. */}
          <Route path="/x/handoff" element={<HandoffPage />} />
          {/* Friendly spinner the extension lands on while the capture
              upload is in flight. The SW opens this URL the moment a
              capture starts so React hydration overlaps with the
              upload, and navigates this tab to /x/ticket?t=<id> on
              success. */}
          <Route path="/x/capturing" element={<CapturingPage />} />
          {/* OAuth consent for the Chrome extension PKCE flow. Requires
              session cookie via the outer RequireAuth wrapper. */}
          <Route path="/oauth/extension" element={<OAuthExtensionPage />} />
          {/* Generic OAuth consent for dynamically-registered MCP clients
              (Claude Code, Claude Desktop, etc). Includes the per-tool
              scope picker. */}
          <Route path="/oauth/authorize" element={<OAuthAuthorizePage />} />
          {/* Team mode's sign-in window, opened by the frame on a customer's
              site (TOADO-548). Requires the session like the two above. */}
          <Route path="/widget-sign-in" element={<WidgetSignInPage />} />
          {/* Admin panel — Toado staff only. Client-side gate via
              RequireStaff (renders NotFound for non-staff so the
              surface is invisible to probes); API also enforces
              requireStaff server-side, so direct fetches from a
              non-staff browser would 404 even if React was bypassed. */}
          <Route element={<RequireStaff />}>
            <Route path="/admin/*" element={<AdminLayout />} />
          </Route>
          <Route element={<RequireV2Recorder />}>
            <Route path="/recordings" element={<RecordingsPage />} />
            <Route path="/recordings/history" element={<RecordingsPage tab="history" />} />
            <Route path="/recordings/:recordingId/review" element={<RecordingReviewPage />} />
          </Route>
        </Route>
      </Route>

      <Route path="/403" element={<ForbiddenPage />} />
      <Route path="/500" element={<ServerErrorPage />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    </div>
    {background && (
      <Routes>
        <Route element={<RequireAuth />}>
          <Route path="/tickets/:ticketId" element={<TicketModal />} />
        </Route>
      </Routes>
    )}
    </>
  );
}
